import { AutoStoriesOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useApi } from '../../../../hooks/useApi';
import { useApiData } from '../../../../hooks/useApiData';
import { useUser } from '../../../../hooks/useUser';
import OpenEnrollmentNotification from '../../../benefits/OpenEnrollmentNotification';
import HandbookSignModal from '../../../dashboard/HandbookSignModal';
import UpdateUserDefinedFieldsModal from '../../../profile/UpdateUserDefinedFieldsModal';
import AppNotification from '../Notifications/AppNotification';
import ClientDropdown from './ClientDropdown';
import HeaderSearch from './HeaderSearch';
import MobileSection from './MobileSection';
import Profile from './Profile';

const HeaderContent: React.FC = () => {
  const downLG = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
  const [dialogOpen, setDialogOpen] = useState<boolean>(true);
  const [signDialogOpen, setSignDialogOpen] = useState<boolean>(false);
  const [customFieldsModalOpen, setCustomFieldsModalOpen] =
    useState<boolean>(false);

  const { data: handbooks, refresh } = useApiData((api) =>
    api.profiles.getMyHandbooks()
  );

  const { user } = useUser();

  const { data: customFields, refresh: refreshCustomFields } = useApiData(
    (api) => api.profiles.getEmployeeCustomFields()
  );

  const api = useApi();
  const [employeeProfile, setEmployeeProfile] =
    useState<EmployeeProfile | null>(null);

  const needsActionHBs = handbooks?.filter((hb) =>
    ['pending', 'overdue'].includes(hb.status)
  );
  const hasOverdue = needsActionHBs?.some((ha) => ha.status === 'overdue');

  const hasRequiredCustomFields = user?.has_required_custom_fields ?? false;
  const customFieldsData: ProfileCustomFieldValue[] = customFields ?? [];

  // Only fetch employee profile for client 623217 when custom fields check is needed
  const isStudentAgentClient = user?.client_id === '623217';
  const needsJobCodeCheck =
    hasRequiredCustomFields &&
    customFieldsData.length > 0 &&
    isStudentAgentClient;

  const fetchEmployeeProfile = useCallback(async () => {
    try {
      const response = await api.profiles.getEmployeeProfile();
      setEmployeeProfile(response.results);
    } catch (error) {
      console.error('Error fetching employee profile:', error);
      setEmployeeProfile(null);
    }
  }, [api]);

  useEffect(() => {
    if (needsJobCodeCheck) {
      fetchEmployeeProfile();
    }
  }, [needsJobCodeCheck, fetchEmployeeProfile]);

  // Determine if user is eligible to see the custom fields modal
  const shouldShowCustomFieldsModal = useMemo(() => {
    if (!hasRequiredCustomFields || customFieldsData.length === 0) {
      return false;
    }

    // For the student agent client, require job code to include "student agent"
    if (isStudentAgentClient) {
      // Wait for employee profile to load before showing modal
      if (!employeeProfile) {
        return false;
      }
      const jobCodeDescription =
        employeeProfile.position?.employee_title?.toLowerCase() ?? '';
      return jobCodeDescription.includes('student agent');
    }

    return true;
  }, [
    hasRequiredCustomFields,
    customFieldsData.length,
    isStudentAgentClient,
    employeeProfile,
  ]);

  useEffect(() => {
    if (shouldShowCustomFieldsModal) {
      setCustomFieldsModalOpen(true);
    }
  }, [shouldShowCustomFieldsModal]);

  return (
    <>
      {!downLG && <HeaderSearch />}
      {downLG && <Box sx={{ width: '100%', ml: 1 }} />}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row',
          ml: 'auto',
          gap: 1.5,
        }}
      >
        <ClientDropdown />
        <OpenEnrollmentNotification />
        <AppNotification />
        {!downLG && <Profile />}
        {downLG && <MobileSection />}
      </Box>
      {needsActionHBs && needsActionHBs.length > 0 && (
        <>
          <Dialog open={dialogOpen} maxWidth="xs">
            <DialogTitle sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                <Typography variant="h4">
                  {hasOverdue
                    ? 'You have overdue handbook assignments!'
                    : 'You have assigned handbooks!'}
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              {hasOverdue && (
                <Typography align="center" sx={{ fontWeight: 'bold', my: 1 }}>
                  IMMEDIATE ACTION REQUIRED
                </Typography>
              )}
              <Typography>
                Please review the handbooks and sign the acknowledgement
                statement to complete the process.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button variant="outlined" onClick={() => setDialogOpen(false)}>
                Close
              </Button>

              <Button
                startIcon={<AutoStoriesOutlined />}
                variant="contained"
                onClick={() => {
                  setDialogOpen(false);
                  setSignDialogOpen(true);
                }}
              >
                Complete now
              </Button>
            </DialogActions>
          </Dialog>
          {signDialogOpen &&
            needsActionHBs.map((hb, idx) => (
              <HandbookSignModal
                key={`${hb.handbook_id}-${idx}`}
                refresh={refresh}
                handbook={hb}
              />
            ))}
        </>
      )}

      {customFieldsModalOpen && (
        <UpdateUserDefinedFieldsModal
          open={customFieldsModalOpen}
          onClose={() => setCustomFieldsModalOpen(false)}
          refreshProfile={refreshCustomFields}
          customFields={customFieldsData}
          isRequired={true}
        />
      )}
    </>
  );
};

export default HeaderContent;
